import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import {
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_HEADLINE_TEXT,
    BRAND_LIBRARY_BODY_TEXT,
} from 'common/constants/brandLibrary';

import Spacer from 'common/components/Spacer';

import { useEditor, useEditingCond, useNode } from '@splashthat/splash-cms-engine';

import SessionAvatar from 'common/components/SessionCard/SessionAvatar';
import { SessionText } from 'common/components/SessionCard/SessionCard.styles';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { useAttendeeSessions } from 'common/components/TouchpointEditor/contexts/AttendeeSessionContext';
import { useModal } from 'common/components/TouchpointEditor/contexts/ModalManager';

import { NODE_ID_SPEAKER_MODALS } from '../../constants';

import {
    SingleSpeakerInformationText,
    RightAlignedSpeakerImages,
    SpeakerInformationContainer,
} from './SpeakerInformation.styles';

export default function SpeakerInformation({ descendants: { speakerName, speakerTitleCompany }, selected, hovered }) {
    const node = useNode();
    const editor = useEditor();
    const {
        theme: { colors, activeFonts },
    } = useTouchpointData();
    const modal = useModal();
    const attendeeSessions = useAttendeeSessions();

    const { speakers } = attendeeSessions.activeSession;
    const [singleSpeaker = {}] = speakers;
    const singleSpeakerFullName = [singleSpeaker.firstName, singleSpeaker.lastName].filter(Boolean).join(' ');
    const singleSpeakerJobTitleAndCompany = [singleSpeaker.jobTitle, singleSpeaker.company].filter(Boolean).join(', ');

    const onClickSpeakerModalIfEditing = () => editor.actions.selectNode(NODE_ID_SPEAKER_MODALS);

    const onClickSpeakerModalIfNotEditing = useCallback(
        (speakerInfo) =>
            modal.open(NODE_ID_SPEAKER_MODALS, {
                speaker: speakerInfo,
            }),
        [],
    );

    const openSpeakerModal = useEditingCond(onClickSpeakerModalIfEditing, onClickSpeakerModalIfNotEditing);

    if (!speakers.length) {
        return null;
    }

    return (
        <SpeakerInformationContainer
            selected={selected}
            hovered={hovered}
            onClick={node.actions.onClick()}
            onMouseLeave={node.actions.onMouseLeave()}
            onMouseMove={node.actions.onMouseMove()}
        >
            <RightAlignedSpeakerImages>
                {speakers.map((speaker) => (
                    <SessionAvatar
                        key={speaker.id}
                        src={speaker.avatarUrl}
                        firstName={speaker.firstName}
                        lastName={speaker.lastName}
                        fontFamily={activeFonts[speakerName.styles.fontFamily]}
                        onClickSpeakerAvatar={() => openSpeakerModal(speaker)}
                        dataQa="session_info_speaker_avatar"
                    />
                ))}
            </RightAlignedSpeakerImages>
            {speakers.length === 1 && (
                <SingleSpeakerInformationText>
                    <Spacer $spacing="0.5rem" />
                    <SessionText
                        $fontSize={speakerName.styles.fontSize}
                        $fontColor={colors[speakerName.styles.fontColor]}
                        $fontFamily={activeFonts[speakerName.styles.fontFamily]}
                        data-qa="session_info_speaker_name"
                    >
                        {singleSpeakerFullName}
                    </SessionText>
                    <Spacer $spacing="0" />
                    <SessionText
                        $fontSize={speakerTitleCompany.styles.fontSize}
                        $fontColor={colors[speakerTitleCompany.styles.fontColor]}
                        $fontFamily={activeFonts[speakerTitleCompany.styles.fontFamily]}
                        data-qa="session_info_speaker_job_info"
                    >
                        {singleSpeakerJobTitleAndCompany}
                    </SessionText>
                </SingleSpeakerInformationText>
            )}
        </SpeakerInformationContainer>
    );
}

SpeakerInformation.splash = {
    related: {
        settings: React.lazy(() => import('./SpeakerInformation.tools')),
    },
    defaultProps: {
        descendants: {
            speakerName: {
                styles: {
                    fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    fontSize: 16,
                    fontFamily: BRAND_LIBRARY_HEADLINE_TEXT,
                },
            },
            speakerTitleCompany: {
                styles: {
                    fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
                    fontSize: 16,
                    fontFamily: BRAND_LIBRARY_BODY_TEXT,
                },
            },
        },
    },
};

SpeakerInformation.propTypes = {
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    descendants: PropTypes.shape({
        speakerName: PropTypes.shape({
            styles: PropTypes.shape({
                fontColor: PropTypes.number.isRequired,
                fontSize: PropTypes.number.isRequired,
                fontFamily: PropTypes.number.isRequired,
            }),
        }),
        speakerTitleCompany: PropTypes.shape({
            styles: PropTypes.shape({
                fontColor: PropTypes.number.isRequired,
                fontSize: PropTypes.number.isRequired,
                fontFamily: PropTypes.number.isRequired,
            }),
        }),
    }).isRequired,
};
